import { CloseButton, HStack, Icon, Stack, Text } from '@chakra-ui/react';
import {
  ActiveAnnouncement,
  AnnouncementColor,
  AnnouncementMessages,
} from '@bofrak-backend/shared';
import { i18n, useTranslation } from '@bofrak-backend/shared-i18n';
import { useState } from 'react';
import { MdCampaign } from 'react-icons/md';
import { useQuery } from 'react-query';
import { SHARED_UI_NS, useSharedUiTranslation } from '../../i18n';
import { apiAdapter } from '../../utils';

/** Background per preset (white text passes WCAG AA on each). */
export const ANNOUNCEMENT_COLORS: Record<AnnouncementColor, string> = {
  [AnnouncementColor.ORANGE]: 'orange.600',
  [AnnouncementColor.RED]: 'red.600',
  [AnnouncementColor.BLUE]: 'blue.600',
  [AnnouncementColor.GREEN]: 'green.700',
  [AnnouncementColor.PURPLE]: 'purple.600',
};

/** A user's ×: hidden for 24 hours, then shown again while still live. */
const DISMISS_MS = 24 * 60 * 60 * 1000;
const STORAGE_KEY = 'sns-announcement-dismissals';

const readDismissals = (): Record<string, number> => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '{}');
  } catch {
    return {};
  }
};

/** One dismissal per version: an edited announcement shows again. */
const dismissalKey = (a: ActiveAnnouncement) => `${a.id}:${a.version}`;

/** The message in the UI language (pt-PT → pt); English otherwise. */
export const announcementMessage = (
  messages: AnnouncementMessages,
  language?: string,
): string => {
  if (language?.startsWith('pt')) return messages.pt;
  if (language?.startsWith('fr')) return messages.fr;
  return messages.en;
};

/** The banner itself; also the live preview in the admin app. */
export const AnnouncementBannerView = ({
  message,
  color,
  onClose,
}: {
  message: string;
  color: AnnouncementColor;
  onClose?: () => void;
}) => {
  const { t } = useSharedUiTranslation();
  return (
    <HStack
      role="status"
      bg={ANNOUNCEMENT_COLORS[color]}
      color="white"
      px={{ base: 3, md: 4 }}
      py={2}
      spacing={3}
      align="center">
      <Icon as={MdCampaign} boxSize={5} flexShrink={0} aria-hidden />
      <Text flex={1} fontSize="sm" fontWeight="medium" whiteSpace="pre-wrap">
        {message}
      </Text>
      {onClose && (
        <CloseButton size="sm" aria-label={t('Dismiss')} onClick={onClose} />
      )}
    </HStack>
  );
};

/**
 * The announcements admins publish (admin app → Settings), for this store;
 * pages without a store get the all-stores ones. Read once per 5 minutes.
 */
export const AnnouncementBanner = ({
  merchantId,
  storeId,
}: {
  merchantId?: string;
  storeId?: string;
}) => {
  const { i18n: instance } = useTranslation(SHARED_UI_NS, {
    i18n,
    useSuspense: false,
  });
  const [dismissals, setDismissals] = useState(readDismissals);
  const where = { merchant_id: merchantId, store_id: storeId };
  const { data } = useQuery(
    ['get-active-announcements', where],
    () => apiAdapter.getActiveAnnouncements(where),
    {
      staleTime: 5 * 60 * 1000,
      refetchInterval: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  );

  const now = Date.now();
  const visible = (data?.announcements ?? []).filter(
    (a) => !(now - (dismissals[dismissalKey(a)] ?? 0) < DISMISS_MS),
  );
  if (!visible.length) return null;

  const dismiss = (announcement: ActiveAnnouncement) => {
    // In memory too: without storage, earlier dismissals still hold.
    const next = {
      ...dismissals,
      ...readDismissals(),
      [dismissalKey(announcement)]: Date.now(),
    };
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
      // Private mode: hidden until the page reloads.
    }
    setDismissals(next);
  };

  return (
    <Stack spacing={0}>
      {visible.map((announcement) => (
        <AnnouncementBannerView
          key={announcement.id}
          color={announcement.color}
          message={announcementMessage(
            announcement.messages,
            instance?.language,
          )}
          onClose={() => dismiss(announcement)}
        />
      ))}
    </Stack>
  );
};
